/**
 * T.10 S7, spec 9.9. Tailoring a resume to a job posting as a word match,
 * no AI. Shared by the subgraph's tailorResume (which reads the vocabulary)
 * and the wizard's resume page (which re-applies the order as the member
 * taps). Nothing here keeps the posting: every function reads it and
 * returns derived values only.
 */

export const TAILOR_POSTING_MAX = 12000;
export const ASK_MAX = 6;
/** WALK-44's resume name limit (resumes.service.ts MAX_RESUME_NAME). */
export const COPY_NAME_MAX = 60;

/**
 * Posting words that name a vocabulary term in other words (spec 9.9, S7
 * changelog). Each points at a real skill or knowledge element; it is used
 * only when that element is in the vocabulary.
 */
export const TAILOR_SYNONYMS: Readonly<Record<string, string>> = {
  'customer service': 'Customer and Personal Service',
  'customer support': 'Customer and Personal Service',
  'customer care': 'Customer and Personal Service',
  troubleshoot: 'Troubleshooting',
  troubleshooting: 'Troubleshooting',
  repair: 'Repairing',
  repairs: 'Repairing',
  computer: 'Computers and Electronics',
  computers: 'Computers and Electronics',
  'equipment maintenance': 'Equipment Maintenance',
  'preventive maintenance': 'Equipment Maintenance',
  install: 'Installation',
  installing: 'Installation',
  instruct: 'Instructing',
  teach: 'Instructing',
  teaching: 'Instructing',
  supervise: 'Management of Personnel Resources',
  supervising: 'Management of Personnel Resources',
  supervision: 'Management of Personnel Resources',
  budget: 'Management of Financial Resources',
  budgets: 'Management of Financial Resources',
  inventory: 'Management of Material Resources',
  'quality control': 'Quality Control Analysis',
  'quality assurance': 'Quality Control Analysis',
  negotiate: 'Negotiation',
  accounting: 'Economics and Accounting',
  bookkeeping: 'Economics and Accounting'
};

export interface TailorInput {
  posting: string;
  /** Skill and knowledge names, technology names, and the member's own words (resume skills, certifications). */
  vocabulary: readonly string[];
  /** Skills on the resume now, in order. */
  resumeSkills: readonly string[];
  /** Certifications on the resume: a posting naming one is asking for something the resume has. */
  certifications?: readonly string[];
  /** Lines per job, in order. */
  jobLines: readonly (readonly string[])[];
  /** Review L5: vocabulary terms that match only as written (see isOrdinaryWord). */
  exactCase?: readonly string[];
}

export interface TailorOrder {
  /** On the resume and in the posting, in posting order. */
  matchedSkills: string[];
  /** In the posting, not on the resume yet (at most 6). Never added without a tap. */
  askSkills: string[];
  /** The resume skills, the matched ones first. */
  skills: string[];
  /** Each job's lines, lines naming posting terms first, wording unchanged. */
  jobLines: string[][];
  /** The lines that name a posting term. */
  promotedLines: string[];
  /** matched / (matched + ask); 0 when the posting names none. */
  coverage: number;
  coverageLine: string;
}

export interface TailorOutput extends TailorOrder {
  postingTitle: string;
  postingEmployer: string;
  /** The vocabulary terms the posting names, in posting order. */
  postingTerms: string[];
}

/** Review H1: a job title longer than this (characters or words) is a sentence, not a title, and is never kept. */
export const TITLE_MAX_CHARS = 80;
export const TITLE_MAX_WORDS = 10;
export const EMPLOYER_MAX_CHARS = 60;

export function cleanPosting(raw: string): string {
  // Review M4: cut first, and a tag pattern that cannot backtrack across "<<<<".
  return (raw ?? '')
    .slice(0, TAILOR_POSTING_MAX)
    .replace(/<[^<>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/[ \t]+/g, ' ')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .join('\n')
    .trim();
}

/** Words whose period never ends the first sentence ("Sr. Technician", "St. Mary Clinic"). */
const NEVER_ENDS = new Set(['sr', 'jr', 'mr', 'mrs', 'ms', 'dr', 'st', 'mt', 'ft', 'lt', 'sgt', 'capt', 'col', 'maj', 'gen', 'no']);
/** Company suffixes: the sentence may end there, and the period is the name's ("Acme Inc."). */
const KEEPS_DOT = new Set(['inc', 'corp', 'co', 'ltd', 'llc', 'llp']);

/** The first sentence of a line: never cut at a title or an initial ("U.S."), and a company suffix keeps its period. */
function firstSentence(line: string): string {
  const ends = /\.(?=\s|$)/g;
  let m: RegExpExecArray | null;
  while ((m = ends.exec(line))) {
    const before = /([A-Za-z]+)$/.exec(line.slice(0, m.index))?.[1] ?? '';
    const word = before.toLowerCase();
    if (NEVER_ENDS.has(word) || before.length === 1) {
      continue;
    }
    return KEEPS_DOT.has(word) ? line.slice(0, m.index + 1) : line.slice(0, m.index);
  }
  return line;
}

const wordCount = (s: string) => s.split(/\s+/).filter(Boolean).length;

/** Review H1: the title as kept, or '' when it reads as a sentence (over 80 characters or 10 words). */
export function capTitle(title: string | null | undefined): string {
  const t = (title ?? '').replace(/\s+/g, ' ').trim();
  return t.length > TITLE_MAX_CHARS || wordCount(t) > TITLE_MAX_WORDS ? '' : t;
}

/** Review H1: the employer as kept, or '' when over 60 characters. */
export function capEmployer(employer: string | null | undefined): string {
  const e = (employer ?? '').replace(/\s+/g, ' ').trim();
  return e.length > EMPLOYER_MAX_CHARS ? '' : e;
}

/** "IT Help Desk Technician", "Lakeview Health" from the posting's first line; '' for either that reads as a sentence. */
export function titleAndEmployer(posting: string): { title: string; employer: string } {
  const line = (posting.split(/\r?\n/).find((l) => l.trim()) ?? '').trim();
  const first = firstSentence(line).trim();
  for (const sep of [' - ', ' at ', ', ']) {
    const i = first.indexOf(sep);
    if (i > 0) {
      return { title: capTitle(first.slice(0, i)), employer: capEmployer(first.slice(i + sep.length)) };
    }
  }
  return { title: capTitle(first), employer: '' };
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const wholeWord = (term: string, exact = false) => new RegExp(`(^|[^A-Za-z0-9])${escape(term)}(?![A-Za-z0-9])`, exact ? '' : 'i');
/** Review L5: a one-word name like "Word" or "Access" could be an ordinary word; it matches only as written. */
export const isOrdinaryWord = (name: string): boolean => /^[A-Z][a-z]+$/.test(name);
const lower = (s: string) => s.toLowerCase();

/**
 * The vocabulary terms a text names, in the order they first appear
 * (synonyms included). Review L5: a term in `exactCase` (a technology name
 * that is also an ordinary word, "Word", "Access") matches only as written.
 */
export function termsIn(text: string, vocabulary: readonly string[], exactCase: ReadonlySet<string> = new Set()): string[] {
  const found = new Map<string, number>();
  const look = (needle: string, term: string) => {
    if (!needle.trim()) {
      return;
    }
    const m = wholeWord(needle, exactCase.has(needle)).exec(text);
    if (m) {
      const at = m.index + m[1].length;
      const was = found.get(term);
      if (was === undefined || was > at) {
        found.set(term, at);
      }
    }
  };
  const known = new Map(vocabulary.map((t) => [lower(t), t]));
  vocabulary.forEach((t) => look(t, t));
  Object.entries(TAILOR_SYNONYMS).forEach(([word, term]) => {
    const t = known.get(lower(term));
    if (t) {
      look(word, t);
    }
  });
  return [...found.entries()].sort((a, b) => a[1] - b[1]).map(([term]) => term);
}

export function coverageLine(matched: number, total: number): string {
  return total
    ? `Your resume names ${matched} of the ${total} skills this posting asks for.`
    : 'This posting does not name skills we can match yet.';
}

/** The order a posting's terms give a resume: used by the server and again on the page as the member taps. */
export function applyTailoring(x: {
  postingTerms: readonly string[];
  resumeSkills: readonly string[];
  certifications?: readonly string[];
  jobLines: readonly (readonly string[])[];
}): TailorOrder {
  const onResume = new Set([...x.resumeSkills, ...(x.certifications ?? [])].map(lower));
  const matched = x.postingTerms.filter((t) => onResume.has(lower(t)));
  const missing = x.postingTerms.filter((t) => !onResume.has(lower(t)));
  const askSkills = missing.slice(0, ASK_MAX);
  const skillMatched = matched
    .map((m) => x.resumeSkills.find((s) => lower(s) === lower(m)))
    .filter((s): s is string => !!s);
  const skills = [...skillMatched, ...x.resumeSkills.filter((s) => !skillMatched.includes(s))];
  const promotedLines: string[] = [];
  const jobLines = x.jobLines.map((lines) => {
    const named = lines.filter((l) => termsIn(l, x.postingTerms).length > 0);
    promotedLines.push(...named);
    return [...named, ...lines.filter((l) => !named.includes(l))];
  });
  // Review L6: every term the posting names counts, not only the chips shown.
  const total = matched.length + missing.length;
  return {
    matchedSkills: matched,
    askSkills,
    skills,
    jobLines,
    promotedLines,
    coverage: total ? matched.length / total : 0,
    coverageLine: coverageLine(matched.length, total)
  };
}

export function tailorResume(input: TailorInput): TailorOutput {
  const posting = cleanPosting(input.posting);
  const { title, employer } = titleAndEmployer(posting);
  const vocabulary = [...input.vocabulary, ...input.resumeSkills, ...(input.certifications ?? [])];
  const seen = new Set<string>();
  const unique = vocabulary.filter((t) => t && !seen.has(lower(t)) && !!seen.add(lower(t)));
  const postingTerms = termsIn(posting, unique, new Set(input.exactCase ?? []));
  return {
    postingTitle: title,
    postingEmployer: employer,
    postingTerms,
    ...applyTailoring({ postingTerms, resumeSkills: input.resumeSkills, certifications: input.certifications, jobLines: input.jobLines })
  };
}

const SMALL_WORDS = new Set(['and', 'or', 'of', 'the', 'a', 'an', 'in', 'for', 'to', 'at', 'with', 'on', 'except', 'all', 'other', 'jr', 'sr', 'senior', 'junior', 'lead', 'i', 'ii', 'iii']);

/** Lower-case words, small words left out, plurals read as singulars. */
function titleWords(title: string): string[] {
  return (title.toLowerCase().match(/[a-z0-9]+/g) ?? [])
    .filter((w) => !SMALL_WORDS.has(w))
    .map((w) => (w.length > 4 && w.endsWith('ies') ? `${w.slice(0, -3)}y` : w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w));
}

/**
 * The occupation a posting title names: one with a title or alternate title
 * whose words are all in the posting title, the title with the most words
 * winning (the first listed on a tie). Null when none fits.
 */
export function occupationForTitle(postingTitle: string, titles: readonly { code: string; title: string }[]): string | null {
  return occupationForPrepared(postingTitle, prepareTitles(titles));
}

/** Review L4: titles split into words once (the server caches these), not on every posting. */
export interface PreparedTitle {
  code: string;
  words: string[];
}

export function prepareTitles(titles: readonly { code: string; title: string }[]): PreparedTitle[] {
  return titles.map((t) => ({ code: t.code, words: titleWords(t.title) })).filter((t) => t.words.length > 0);
}

export function occupationForPrepared(postingTitle: string, titles: readonly PreparedTitle[]): string | null {
  const words = new Set(titleWords(postingTitle));
  if (!words.size) {
    return null;
  }
  let best: { code: string; n: number } | null = null;
  for (const t of titles) {
    const need = t.words;
    if (need.every((w) => words.has(w)) && (!best || need.length > best.n)) {
      best = { code: t.code, n: need.length };
    }
  }
  return best?.code ?? null;
}

/** "IT Help Desk Technician, Lakeview Health", cut to WALK-44's 60 characters. */
export function tailoredCopyName(title: string, employer: string): string {
  const name = [capTitle(title), capEmployer(employer)].filter(Boolean).join(', ') || 'Tailored resume';
  return name.length > COPY_NAME_MAX ? name.slice(0, COPY_NAME_MAX).trim() : name;
}
